import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Box,
  LinearProgress,
  Collapse,
  IconButton,
  Tooltip,
  Chip,
} from "@material-ui/core";
import {
  CheckCircle,
  RadioButtonUnchecked,
  Close,
  ExpandLess,
  ExpandMore,
  ArrowForward,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: 12,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    background: theme.palette.type === "dark"
      ? "rgba(37,211,102,0.12)"
      : "rgba(37,211,102,0.08)",
    cursor: "pointer",
    userSelect: "none",
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    flex: 1,
    color: theme.palette.text.primary,
  },
  progressText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  },
  progressBar: {
    height: 4,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.1)"
        : "rgba(0,0,0,0.08)",
  },
  progressFill: {
    backgroundColor: "#25D366",
  },
  body: {
    padding: theme.spacing(0.5, 0),
  },
  item: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.2, 2),
    gap: theme.spacing(1.5),
    cursor: ({ done }) => (done ? "default" : "pointer"),
    transition: "background 0.15s",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  itemIcon: {
    flexShrink: 0,
  },
  itemText: {
    flex: 1,
  },
  itemTitle: {
    fontSize: "0.88rem",
    fontWeight: 500,
    color: ({ done }) =>
      done ? theme.palette.text.disabled : theme.palette.text.primary,
    textDecoration: ({ done }) => (done ? "line-through" : "none"),
  },
  itemDesc: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  arrow: {
    color: theme.palette.text.disabled,
    fontSize: 16,
  },
  doneAll: {
    padding: theme.spacing(1.5, 2),
    textAlign: "center",
    fontSize: "0.85rem",
    color: "#25D366",
    fontWeight: 600,
  },
}));

const STORAGE_KEY = "me_onboarding_dismissed";

const CheckItem = ({ item, classes }) => {
  const history = useHistory();
  const itemClasses = makeStyles((theme) => ({
    item: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(1.2, 2),
      gap: theme.spacing(1.5),
      cursor: item.done ? "default" : "pointer",
      transition: "background 0.15s",
      "&:hover": {
        background: item.done ? "transparent" : theme.palette.action.hover,
      },
    },
    itemTitle: {
      fontSize: "0.88rem",
      fontWeight: 500,
      color: item.done ? theme.palette.text.disabled : theme.palette.text.primary,
      textDecoration: item.done ? "line-through" : "none",
    },
    itemDesc: {
      fontSize: "0.75rem",
      color: theme.palette.text.secondary,
    },
  }))();

  const handleClick = () => {
    if (!item.done && item.route) history.push(item.route);
  };

  return (
    <div className={itemClasses.item} onClick={handleClick}>
      {item.done ? (
        <CheckCircle style={{ color: "#25D366", fontSize: 20, flexShrink: 0 }} />
      ) : (
        <RadioButtonUnchecked
          style={{ color: "#bdbdbd", fontSize: 20, flexShrink: 0 }}
        />
      )}
      <div style={{ flex: 1 }}>
        <Typography className={itemClasses.itemTitle}>{item.label}</Typography>
        {!item.done && item.desc && (
          <Typography className={itemClasses.itemDesc}>{item.desc}</Typography>
        )}
      </div>
      {!item.done && item.route && (
        <ArrowForward style={{ color: "#bdbdbd", fontSize: 16, flexShrink: 0 }} />
      )}
    </div>
  );
};

const OnboardingChecklist = ({ counters }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(
    () => !!localStorage.getItem(STORAGE_KEY)
  );
  const [hasWhatsapp, setHasWhatsapp] = useState(false);
  const [hasQueueLinked, setHasQueueLinked] = useState(false);
  const [hasQueue, setHasQueue] = useState(false);
  const [hasUser, setHasUser] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [wpRes, qRes, uRes] = await Promise.all([
        api.get("/whatsapp/?session=0"),
        api.get("/queue"),
        api.get("/users"),
      ]);
      const connections = wpRes.data || [];
      setHasWhatsapp(connections.some((w) => w.status === "CONNECTED"));
      // Fila vinculada = conexão conectada que tem ao menos 1 fila atrelada
      setHasQueueLinked(
        connections.some(
          (w) => w.status === "CONNECTED" && w.queues && w.queues.length > 0
        )
      );
      setHasQueue(qRes.data.length > 0);
      // more than 1 user means an attendant was added beyond the admin
      setHasUser((uRes.data?.users ?? uRes.data ?? []).length > 1);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!dismissed) loadData();
  }, [dismissed, loadData]);

  if (dismissed) return null;

  const hasSentMessage =
    (counters?.supportFinished ?? 0) > 0 ||
    (counters?.supportHappening ?? 0) > 0;

  const items = [
    {
      label: "Conta criada",
      desc: null,
      done: true,
      route: null,
    },
    {
      label: "Criar uma fila de atendimento",
      desc: "Crie um departamento (ex: Vendas, Suporte) antes de conectar o WhatsApp",
      done: hasQueue,
      route: "/queues",
    },
    {
      label: "Conectar WhatsApp e vincular a fila",
      desc: "Escaneie o QR Code e vincule a fila criada — sem isso, mensagens não são roteadas",
      done: hasQueueLinked,
      route: "/connections",
    },
    {
      label: "Adicionar um atendente",
      desc: "Convide sua equipe em Usuários e associe às filas",
      done: hasUser,
      route: "/users",
    },
    {
      label: "Enviar a primeira mensagem",
      desc: "Inicie uma conversa em Atendimentos",
      done: hasSentMessage,
      route: "/tickets",
    },
  ];

  const doneCount = items.filter((i) => i.done).length;
  const total = items.length;
  const percent = Math.round((doneCount / total) * 100);
  const allDone = doneCount === total;

  return (
    <Paper className={classes.root} elevation={0}>
      <div
        className={classes.header}
        onClick={() => setExpanded((p) => !p)}
      >
        <Typography className={classes.headerTitle}>
          🚀 Primeiros passos
        </Typography>
        <Typography className={classes.progressText}>
          {doneCount}/{total}
        </Typography>
        <Chip
          label={`${percent}%`}
          size="small"
          style={{
            backgroundColor: allDone ? "#25D366" : "#1976d2",
            color: "#fff",
            fontWeight: 700,
            height: 20,
            marginRight: 8,
          }}
        />
        <Tooltip title="Dispensar">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              localStorage.setItem(STORAGE_KEY, "1");
              setDismissed(true);
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
        {expanded ? (
          <ExpandLess fontSize="small" style={{ marginLeft: 4 }} />
        ) : (
          <ExpandMore fontSize="small" style={{ marginLeft: 4 }} />
        )}
      </div>

      <LinearProgress
        variant="determinate"
        value={percent}
        classes={{
          root: classes.progressBar,
          bar: classes.progressFill,
        }}
      />

      <Collapse in={expanded}>
        <div className={classes.body}>
          {allDone ? (
            <Typography className={classes.doneAll}>
              ✅ Parabéns! Você completou a configuração inicial.
            </Typography>
          ) : (
            items.map((item, i) => (
              <CheckItem key={i} item={item} classes={classes} />
            ))
          )}
        </div>
      </Collapse>
    </Paper>
  );
};

export default OnboardingChecklist;
